import React, { useState } from 'react'
import { useFormatTime } from '~/hooks/useFormatTime'
import { useTranslation } from '~/hooks/useTranslation'
import { Button } from '~/lib/ui/button'
import {
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ArrowUpRightIcon,
} from '@heroicons/react/24/outline'
import { Bicycle } from 'iconoir-react'
import { DownloadIcon } from '~/components/job_search/SavedRecruitersPage/Icons'
import { CompassIcon } from 'lucide-react'

const PostWebinarResourceListItem = ({ resource }) => {
  const [expandDetails, setExpandDetails] = useState(false)
  const { locale } = useTranslation()
  const formatTime = useFormatTime(locale)
  const { t } = useTranslation('PostWebinarResourceListItem')

  return (
    <div className="event-item my-6 !mr-0 grid grid-cols-1 !rounded-xl border-2 !py-0">
      <div className="grid grid-cols-12 gap-4 rounded-t-xl border-gray-500">
        <div className="col-span-12 self-center px-8 pb-4 pt-8">
          <div className="mb-4 flex items-center gap-2 text-xl font-bold text-intoo-green-coachcta">
            <DocumentTextIcon className="h-6 w-6 align-middle" />
            {resource.webinar_type}
          </div>
          <p className="text-cod-gray-500">
            {t('presented_by', { name: resource.coach_name })}
          </p>
          <p className="flex items-center text-cod-gray-500">
            <CalendarIcon className="h-6 w-6 align-middle" />
            <span className="ml-2">{formatTime(resource.start_at)}</span>
          </p>
          <p className="workshops-item-ele">
            {t('thank_you_for_attending', {
              webinar_type: resource.webinar_type,
            })}
          </p>
          <div className="mb-4 flex items-center">
            {resource.webinar_rating === null ? (
              <a
                href={resource.feedback_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rate this webinar link"
              >
                {t('please_share_feedback')}
              </a>
            ) : (
              <div className="flex gap-2" aria-label="Star rating">
                {t('thank_you_for_feedback')}
                {[1, 2, 3, 4, 5].map((star, idx) => (
                  <svg
                    key={star}
                    className={`${idx === 0 ? 'ml-2' : 'ml-0'} h-6 w-6 cursor-pointer transition-colors duration-150 ${resource.webinar_rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill={
                      resource.webinar_rating >= star ? 'currentColor' : 'none'
                    }
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <polygon points="12 17.27 18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21 12 17.27" />
                  </svg>
                ))}
                (
                {resource.webinar_rating > 1
                  ? t('stars', { count: resource.webinar_rating })
                  : t('star', { count: resource.webinar_rating })}
                )
              </div>
            )}
          </div>
          <Button
            variant={expandDetails ? 'coach_linkedin' : 'coachcta'}
            onClick={() => setExpandDetails((prev) => !prev)}
          >
            {expandDetails ? (
              <>
                <ChevronUpIcon className="mr-2 h-6 w-6" /> {t('hide_resources')}
              </>
            ) : (
              <>
                <ChevronDownIcon className="mr-2 h-6 w-6" />{' '}
                {resource.post_webinar_resources.length > 1
                  ? t('show_resources', {
                      count: resource.post_webinar_resources.length,
                    })
                  : t('show_resource')}
              </>
            )}
          </Button>
        </div>
      </div>
      {expandDetails && (
        <div className="rounded-b-xl border-gray-500 bg-gray-100 p-8">
          <div className="text-xl font-bold">{t('post_webinar_resources')}</div>
          <div className="grid grid-cols-2 gap-4">
            {resource.post_webinar_resources.map((resourceItem, index) => (
              <a
                href={resourceItem.url}
                target="_blank"
                rel="noopener noreferrer"
                key={index}
              >
                <div className="mt-4 flex justify-between rounded-xl border-gray-500 bg-white p-4">
                  <div className="flex items-center">
                    <div className="rounded-xl bg-lily-blue p-4">
                      {resourceItem.resource_type === 'Document' && (
                        <DocumentTextIcon className="inline-block h-6 w-6  text-primary" />
                      )}
                      {resourceItem.resource_type === 'Checklist' && (
                        <ClipboardDocumentListIcon className="inline-block h-6 w-6 text-primary" />
                      )}
                      {resourceItem.resource_type === 'Article' && (
                        <CompassIcon className="inline-block h-6 w-6 text-primary" />
                      )}
                      {resourceItem.resource_type === 'Exercise' && (
                        <Bicycle className="inline-block h-6 w-6 text-primary" />
                      )}
                    </div>
                    <span className="ml-2">
                      {resource.post_webinar_resources.length > 1
                        ? resource.post_webinar_resources[index].name
                        : resource.post_webinar_resources[0].name}
                    </span>
                  </div>

                  {resourceItem.resource_type === 'Document' && (
                    <DownloadIcon className="my-auto h-6 w-6 text-cod-gray-500" />
                  )}
                  {resourceItem.resource_type !== 'Document' && (
                    <ArrowUpRightIcon className="my-auto h-6 w-6 text-cod-gray-500" />
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PostWebinarResourceListItem
